import { toast } from "react-toastify";

const DEFAULT_PHONE_NUMBER_ID = "1330037596851669";
const DEFAULT_ACCESS_TOKEN = "EAAXyFFsFHxcBRqaZCo8iZBpAS6ieCweVLab1DUZC65m5IwcoH5bF9IAItl9BGooOMCW1p5aLm1Ic3RNxZBYuqOWa4Bqb65sNnkMDB6wgmeFEhcG33ojESzaH11cQkqf4Fa0D87U9L4ZAcDE0C38h4CWv1k6cmrZACr6ZC1c9u5W6JZC2HfsXEXpHLfDq445HXwZDZD";

/**
 * Clean and format recipient phone number for WhatsApp Cloud API.
 * Ensures 10-digit Indian numbers are prepended with country code "91".
 */
export const formatWhatsAppNumber = (phone) => {
  if (!phone) return "";
  const cleanNumber = String(phone).replace(/\D/g, "").replace(/^0+/, "");
  if (cleanNumber.length === 10) {
    return "91" + cleanNumber;
  }
  return cleanNumber;
};

/**
 * Send WhatsApp template notification (taqwa_fashion_store or taqwa_perfumes)
 * via Meta Graph API v20.0 with automatic ETP/AdBlocker proxy fallback.
 *
 * @param {Object} params
 * @param {string} params.to - Recipient phone or WhatsApp number
 * @param {string} params.customerName - Customer name parameter
 * @param {string} params.trackingId - Tracking ID parameter
 * @param {string} [params.templateName] - "taqwa_fashion_store" or "taqwa_perfumes"
 * @param {boolean} [params.isPerfumes] - Fallback flag to choose taqwa_perfumes if templateName not specified
 */
export const sendWhatsAppNotification = async ({
  to,
  customerName = "Customer",
  trackingId = "",
  templateName,
  isPerfumes = false,
}) => {
  const formattedNumber = formatWhatsAppNumber(to);
  if (!formattedNumber) {
    toast.error("Please enter a valid Phone or WhatsApp number!");
    return { success: false, error: "Invalid phone number" };
  }

  if (!trackingId || !String(trackingId).trim()) {
    toast.warning("Please enter a Tracking ID before sending to WhatsApp!");
    return { success: false, error: "Missing tracking ID" };
  }

  const finalTemplateName =
    templateName || (isPerfumes ? "taqwa_perfumes" : "taqwa_fashion_store");

  const phoneId =
    process.env.REACT_APP_WHATSAPP_PHONE_NUMBER_ID || DEFAULT_PHONE_NUMBER_ID;
  const accessToken =
    process.env.REACT_APP_WHATSAPP_TOKEN || DEFAULT_ACCESS_TOKEN;

  const payload = {
    messaging_product: "whatsapp",
    recipient_type: "individual",
    to: formattedNumber,
    type: "template",
    template: {
      name: finalTemplateName,
      language: {
        code: "en",
      },
      components: [
        {
          type: "body",
          parameters: [
            {
              type: "text",
              parameter_name: "customer_name",
              text: String(customerName).trim() || "Customer",
            },
            {
              type: "text",
              parameter_name: "tracking_id",
              text: String(trackingId).trim(),
            },
          ],
        },
      ],
    },
  };

  toast.info(`Sending WhatsApp template (${finalTemplateName})...`);

  const directMetaUrl = `https://graph.facebook.com/v20.0/${phoneId}/messages`;
  const edgeFuncUrl = `https://ecvdsyezeauzidjqmpyc.supabase.co/functions/v1/send-whatsapp`;

  // Target URLs to try sequentially (Direct Meta API -> Supabase Edge Function Backend)
  const targetUrls = [
    { url: directMetaUrl, type: "direct" },
    { url: edgeFuncUrl, type: "edge" },
  ];

  let lastError = null;

  for (let i = 0; i < targetUrls.length; i++) {
    const target = targetUrls[i];
    const isEdgeFunc = target.type === "edge";

    const supabaseAnonKey =
      process.env.REACT_APP_SUPABASE_KEY ||
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVjdmRzeWV6ZWF1emlkanFtcHljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjEzOTY5MDQsImV4cCI6MjAzNjk3MjkwNH0.i3-aXlpiUb7vKQSgjl4hIzUK87zdkDCU9ViUNSVdVDg";

    const requestHeaders = isEdgeFunc
      ? {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseAnonKey}`,
        }
      : {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        };

    const requestBody = isEdgeFunc
      ? JSON.stringify({
          to: formattedNumber,
          customerName: String(customerName).trim() || "Customer",
          trackingId: String(trackingId).trim(),
          templateName: finalTemplateName,
        })
      : JSON.stringify(payload);

    try {
      const response = await fetch(target.url, {
        method: "POST",
        headers: requestHeaders,
        body: requestBody,
      });

      let resData = {};
      try {
        resData = await response.json();
      } catch (jsonErr) {
        resData = {};
      }

      if (
        response.ok &&
        (resData.messages || resData.messaging_product || resData.id)
      ) {
        toast.success("WhatsApp message sent successfully!");
        return { success: true, data: resData };
      }

      // If we got a response from Meta (HTTP 4xx/5xx API error), report exact Meta error and don't retry proxies
      if (response.status >= 400 && response.status < 500 && resData.error) {
        console.error("WhatsApp Cloud API error response:", resData);
        const errDetails = JSON.stringify(resData.error, null, 2);
        alert(
          `❌ WhatsApp API Error (HTTP ${response.status}):\n\n${errDetails}`
        );
        toast.error(
          `Failed to send WhatsApp (HTTP ${response.status}): ${
            resData.error?.message || "Check alert for details"
          }`
        );
        return { success: false, error: resData };
      }
    } catch (err) {
      console.warn(
        `Attempt ${i + 1} (${target.type}) to ${target.url} failed due to network/browser blocking:`,
        err
      );
      lastError = err;
      // Continue to next fallback in loop!
    }
  }

  // If all attempts failed (browser blocked all connections):
  console.error("All WhatsApp dispatch attempts failed:", lastError);
  alert(
    `❌ WhatsApp Connection Error:\n\nType: ${lastError?.name || "TypeError"}\nMessage: ${
      lastError?.message || "Network error"
    }\n\nFirefox Enhanced Tracking Protection or an extension blocked direct connection to graph.facebook.com.`
  );
  toast.error(
    `Failed to send WhatsApp: ${lastError?.message || "Network error"}`
  );
  return { success: false, error: lastError };
};
